import { motion, useScroll, useTransform } from "framer-motion";
import { Download, ArrowRight } from "lucide-react";
import { APP_DOWNLOAD_URL, APP_SIZE, APP_VERSION } from "../config";
import { useSmoothScroll } from "./SmoothScroll";
import PhoneMockup from "./PhoneMockup";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const scrollTo = useSmoothScroll();
  const { scrollY } = useScroll();
  const yPhone = useTransform(scrollY, [0, 900], [0, -90]);
  const yGlow = useTransform(scrollY, [0, 900], [0, 140]);

  return (
    <section id="home" className="relative overflow-hidden pt-[120px] pb-16 sm:pt-[150px]" data-testid="hero-section">
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0 gold-glow-top" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-grid-faint [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.div {...fadeUp(0.05)} className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" aria-hidden="true" />
            <span className="font-mono text-[11px] sm:text-xs tracking-[0.32em] text-[#D4AF37] uppercase">
              AI-Powered Wealth Ecosystem
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04] text-white">
            <MaskedLine delay={0.18}>The Future of</MaskedLine>
            <MaskedLine delay={0.3}>
              Digital Wealth <span className="text-gold-gradient">Starts</span>
            </MaskedLine>
            <MaskedLine delay={0.42}>
              <span className="text-gold-gradient">Here.</span>
            </MaskedLine>
          </h1>

          <motion.p {...fadeUp(0.62)} className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#99A1AF]">
            Winter Protocol is building an AI-powered financial ecosystem designed to make
            investing, digital assets and financial tools more accessible through intelligent technology.
          </motion.p>

          <motion.div {...fadeUp(0.74)} className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={APP_DOWNLOAD_URL}
              download="winter-protocol.apk"
              data-testid="hero-download-app-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] px-7 py-3.5 text-sm font-semibold text-[#08080A] transition-all duration-300 hover:bg-[#F3E5AB] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
            >
              <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              Download App
            </a>
            <button
              onClick={() => scrollTo?.("#ecosystem")}
              data-testid="hero-explore-ecosystem-button"
              className="group inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:border-white/50 hover:bg-white/5"
            >
              Explore Ecosystem
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>

          <motion.p {...fadeUp(0.8)} className="mt-4 font-mono text-[10px] tracking-[0.22em] text-[#3A3F4A] uppercase" data-testid="hero-version-badge">
            v{APP_VERSION} · {APP_SIZE} · Android APK
          </motion.p>

          <motion.p {...fadeUp(0.86)} className="mt-6 font-mono text-[11px] tracking-[0.22em] text-[#636B78] uppercase">
            AI-Powered. Community-Driven. Built for the Future.
          </motion.p>
        </div>

        <motion.div
          style={{ y: yPhone }}
          initial={{ opacity: 0, y: 60, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto"
        >
          <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.14)_0%,transparent_65%)]" aria-hidden="true" />
          <PhoneMockup />
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="absolute -left-6 top-16 hidden sm:block glass-card px-4 py-3"
          >
            <p className="font-mono text-[9px] tracking-[0.2em] text-[#636B78] uppercase">WIN Token</p>
            <p className="mt-1 font-display text-sm font-bold text-white">150M Fixed Supply</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="absolute -right-4 bottom-24 hidden sm:block glass-card px-4 py-3"
          >
            <p className="font-mono text-[9px] tracking-[0.2em] text-[#636B78] uppercase">Payout</p>
            <p className="mt-1 font-display text-sm font-bold text-emerald-400">Weekly · Active</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
